import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { processBatteryData, sortOptions, useBatteryData } from '../../hooks/useBatteryData'

function BatteryDropdowns({ 
  suffix = '', 
  watch, 
  setValue,
  register,
  onBlur,
  getFieldError,
  voltageFieldName = 'batteryVoltage', 
}) {
  // Constants to avoid recreating on each render
  const VOLTAGE_PREFIXES = useMemo(() => [
    'cvVolts',
    'postCvVolts', 
    'floatVolts',
    'floatMinVolts',
    'tempSlope',
  ], [])

  // Use form state instead of local state for persistence
  const formData = watch()
  const selectedType = formData[`batteryQuickType${suffix}`] || ''
  const selectedBrand = formData[`batteryQuickBrand${suffix}`] || ''
  const selectedModel = formData[`batteryQuickModel${suffix}`] || ''
  
  const [availableBrands, setAvailableBrands] = useState([])
  const [availableModels, setAvailableModels] = useState([])

  const { data: batteryData, isLoading, error } = useBatteryData()
  
  // Memoize expensive data processing
  const { dropdownTypes, batteryData: rawBatteryData } = useMemo(() => 
    processBatteryData(batteryData), [batteryData])
  
  // Memoize sorted types array
  const sortedTypes = useMemo(() => 
    dropdownTypes ? sortOptions(Object.keys(dropdownTypes)) : [], [dropdownTypes])

  const selectedVoltage = watch(voltageFieldName + suffix) || '12'

  const handleFieldBlur = useCallback((fieldName) => (e) => {
    if (onBlur) {
      onBlur(fieldName, e.target.value)
    }
  }, [onBlur])

  // Initialize dropdown data when form data is loaded or when selected values change
  useEffect(() => {
    if (!dropdownTypes) return

    // Initialize brands when type is loaded
    if (selectedType && dropdownTypes[selectedType]) {
      const brands = Object.keys(dropdownTypes[selectedType])
      setAvailableBrands(sortOptions(brands))
      
      // Initialize models when brand is also loaded
      if (selectedBrand && dropdownTypes[selectedType][selectedBrand]) {
        const {models} = dropdownTypes[selectedType][selectedBrand]
        setAvailableModels(sortOptions(models))
      } else {
        setAvailableModels([])
      }
    } else {
      setAvailableBrands([])
      setAvailableModels([])
    }
  }, [selectedType, selectedBrand, dropdownTypes])

  const renderFieldError = useCallback((fieldName) => {
    const error = getFieldError ? getFieldError(fieldName) : {}
    return error && error.message && <div className="text-danger">{error.message}</div>
  }, [getFieldError])

  // Handle type selection
  const handleTypeChange = useCallback((type) => {
    setValue(`batteryQuickType${suffix}`, type)
    
    // Use startTransition for non-urgent updates
    startTransition(() => {
      setValue(`batteryQuickBrand${suffix}`, '')
      setValue(`batteryQuickModel${suffix}`, '')
      
      if (type && dropdownTypes[type]) {
        const brands = Object.keys(dropdownTypes[type])
        setAvailableBrands(sortOptions(brands))
      } else {
        setAvailableBrands([])
      }
      setAvailableModels([])
    })
  }, [dropdownTypes, setValue, suffix])

  // Handle brand selection
  const handleBrandChange = useCallback((brand) => {
    setValue(`batteryQuickBrand${suffix}`, brand)
    
    // Use startTransition for non-urgent model updates
    startTransition(() => {
      setValue(`batteryQuickModel${suffix}`, '')
      
      if (selectedType && brand && dropdownTypes[selectedType][brand]) {
        const {models} = dropdownTypes[selectedType][brand]
        setAvailableModels(sortOptions(models))
        
        // Auto-select "Standard" if available for "Standard" brand
        if (brand === 'Standard' && models.includes('Standard')) {
          setValue(`batteryQuickModel${suffix}`, 'Standard')
        }
      } else {
        setAvailableModels([])
      }
    })
  }, [selectedType, dropdownTypes, setValue, suffix])

  // Reusable dropdown renderer to eliminate repetition
  const renderDropdown = useCallback((id, label, value, onChange, options, disabled = false, placeholder = 'Select...', registerProps = {}) => (
    <div className="col-md-2">
      <div className="mb-3">
        <label htmlFor={id} className="form-label">{label}</label>
        <select
          className="form-select"
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...registerProps}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
    </div>
  ), [])

  // Apply battery configuration
  const applyConfiguration = useCallback(() => {
    if (!selectedType || !selectedBrand || !selectedModel || !rawBatteryData) {
      return
    }

    const filteredData = Object.values(rawBatteryData).filter(
      (item) =>
        item.type === selectedType &&
        item.brand === selectedBrand &&
        item.model === selectedModel,
    )

    if (filteredData.length === 0) return

    const configData = filteredData[0]
    const voltageMultiplier = parseFloat(selectedVoltage) / 12

    // Set the battery type in the main form
    if (setValue) {
      setValue(`batteryType${suffix}`, selectedType)
    }

    // Apply configuration to form
    Object.keys(configData).forEach(key => {
      // Skip 'type', 'brand', and 'model' fields as they're metadata, not form fields
      if (['type', 'brand', 'model'].includes(key)) {
        return
      }

      let value = configData[key]
      let fieldName = key

      // Adjust voltage-dependent values
      if (VOLTAGE_PREFIXES.some(prefix => key.startsWith(prefix))) {
        value *= voltageMultiplier
      }

      // Add suffix for second battery bank
      if (suffix) {
        fieldName = key + suffix
      }

      // Set the value using React Hook Form
      if (setValue) {
        setValue(fieldName, value)
      }
    })
  }, [selectedType, selectedBrand, selectedModel, rawBatteryData, selectedVoltage, setValue, suffix, VOLTAGE_PREFIXES])

  if (isLoading) {
    return (
      <div className="text-center">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading battery configurations...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="bi bi-exclamation-triangle"></i> Unable to load battery configurations.
      </div>
    )
  }

  return (
    <div className="battery-dropdowns">
      <h5>Quick Battery Configuration{suffix && ` ${suffix}`}</h5>
      <p className="text-muted">
        Select your battery type, brand, and model to automatically populate configuration values.
      </p>      <div className="row">
        <div className="col-md-2">
          <div className="mb-3">
            <label htmlFor={`batteryVoltage${suffix}`} className="form-label">
              Battery Voltage
            </label>
            <select
              className={`form-select ${getFieldError && getFieldError(`batteryVoltage${suffix}`) ? 'is-invalid' : ''}`}
              id={`batteryVoltage${suffix}`}
              {...register(`batteryVoltage${suffix}`)}
              onBlur={handleFieldBlur(`batteryVoltage${suffix}`)}
            >
              <option value="">Select voltage...</option>
              <option value="12">12V</option>
              <option value="24">24V</option>
              <option value="48">48V</option>
            </select>
            {renderFieldError(`batteryVoltage${suffix}`)}
          </div>
        </div>

        {renderDropdown(
          `batteryQuickType${suffix}`,
          'Battery Type',
          selectedType,
          (e) => handleTypeChange(e.target.value),
          sortedTypes,
          false,
          'Select type...',
          register(`batteryQuickType${suffix}`),
        )}

        {renderDropdown(
          `batteryQuickBrand${suffix}`,
          'Brand',
          selectedBrand,
          (e) => handleBrandChange(e.target.value),
          availableBrands,
          !selectedType,
          'Select brand...',
          register(`batteryQuickBrand${suffix}`),
        )}

        {renderDropdown(
          `batteryQuickModel${suffix}`,
          'Model',
          selectedModel,
          (e) => setValue(`batteryQuickModel${suffix}`, e.target.value),
          availableModels,
          !selectedBrand,
          'Select model...',
          register(`batteryQuickModel${suffix}`),
        )}

        <div className="col-md-4">
          <div className="mb-3">
            <label htmlFor={`applyConfig${suffix}`} className="form-label">
              Apply Configuration
            </label>
            <br />
            <button
              type="button"
              className="btn btn-primary"
              onClick={applyConfiguration}
              disabled={!selectedType || !selectedBrand || !selectedModel}
            >
              <i className="bi bi-check-circle"></i> Apply Configuration
            </button>
          </div>
        </div>
      </div>

      <div className="alert alert-info" role="alert">
        <i className="bi bi-info-circle"></i> 
        <strong> Note:</strong> Applying a configuration will overwrite current battery settings. 
        Values will be automatically adjusted for your selected {selectedVoltage}V system.
      </div>

      <div className="alert alert-warning" role="alert">
        <i className="bi bi-exclamation-triangle"></i> 
        <strong> Disclaimer:</strong> These settings are provided based on battery manufacturer recommendations for convenience and we make no representation as to the functionality, safety or compatibility of these batteries.
        <br />
        <strong>Do not use non-CAN bus connected lithium batteries in systems above 12V.</strong>
      </div>
    </div>
  )
}

export default BatteryDropdowns
